import type { QueryClient } from '@tanstack/react-query';

import { assignAndGetAllQuestsUserQuestsAssignAndGetGet } from '../../client';
import { preserveOptimisticQuestState } from './optimisticQuestState';

export const removeAllQuestCaches = (queryClient: QueryClient): void => {
  queryClient.removeQueries({ queryKey: ['allQuests'] });
  queryClient.removeQueries({ queryKey: ['dailyQuests'] });
  queryClient.removeQueries({ queryKey: ['hourlyQuests'] });
  queryClient.removeQueries({ queryKey: ['marketingQuests'] });
  queryClient.removeQueries({ queryKey: ['mainQuests'] });
  queryClient.removeQueries({ queryKey: ['repeatableQuests'] });
};

let inFlightPromise: Promise<void> | null = null;

export const prefetchAllQuests = (queryClient: QueryClient): Promise<void> => {
  if (inFlightPromise) {
    return inFlightPromise;
  }

  inFlightPromise = (async () => {
    const allQuests = await (async () => {
      const { data } = await assignAndGetAllQuestsUserQuestsAssignAndGetGet({ throwOnError: true });
      return data;
    })();    // This response may have left the server before the player watched the ad or
    // claimed the reward, so it must never overwrite what they already earned
    // locally — see preserveOptimisticQuestState.
    queryClient.setQueryData(['dailyQuests'], {
      message: allQuests.message,
      quests: preserveOptimisticQuestState(allQuests.daily),
    });
    queryClient.setQueryData(['hourlyQuests'], {
      message: allQuests.message,
      quests: preserveOptimisticQuestState(allQuests.hourly),
    });
    queryClient.setQueryData(['marketingQuests'], {
      message: allQuests.message,
      quests: preserveOptimisticQuestState(allQuests.marketing),
    });
    queryClient.setQueryData(['mainQuests'], {
      message: allQuests.message,
      quests: preserveOptimisticQuestState(allQuests.main),
    });
    queryClient.setQueryData(['repeatableQuests'], {
      message: allQuests.message,
      quests: preserveOptimisticQuestState(allQuests.repeatable),
    });
    queryClient.setQueryData(['allQuests'], allQuests);
  })()
    .finally(() => {
      inFlightPromise = null;
    });

  return inFlightPromise;
};
